// src/components/tools/LaneConnectorTool/index.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActionBar, Button, HStack, Separator, Text, VStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { matchKeyInText } from "../../../utilies/Utility";
import { setLaneConnectorAutoAdvance, toggleLaneConnectorTool } from "../../../utilies/laneConnector/Add";
import { LuLink, LuZap } from "react-icons/lu";

const LANE_MARKER_KEY = "lanemarker";
const VIRTUAL_LINE_KEY = "virtualline";

function LaneConnectorTool() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const [autoMode, setAutoMode] = useState(true);
  const settings = useAppStore((s) => s.settings);
  const userData = useAppStore((s) => s.userData);

  const laneConnectorMode = useAppStore((s) => s.laneConnectorMode);
  const setLaneConnectorMode = useAppStore((s) => s.setLaneConnectorMode);

  const setCanOpenInfobox = useAppStore((s) => s.setCanOpenInfobox);
  const setCanHightlightEntity = useAppStore((s) => s.setCanHightlightEntity);

  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();

  const [isShow, setIsShow] = useState(false);
  const dsRef = useRef({ lanemarker: null, virtualline: null });

  const findDataSources = useCallback(() => {
    const t = { lanemarker: null, virtualline: null };
    const arr = Array.isArray(mapObjects) ? mapObjects : [];

    for (const obj of arr) {
      if (!obj?.file_name) continue;
      if (matchKeyInText(obj.file_name, LANE_MARKER_KEY)) t.lanemarker = obj.dataSource;
      if (matchKeyInText(obj.file_name, VIRTUAL_LINE_KEY)) t.virtualline = obj.dataSource;
    }
    return t;
  }, [mapObjects]);

  useEffect(() => {
    const t = findDataSources();
    dsRef.current = t;
    setIsShow(Boolean(t.lanemarker || t.virtualline));
  }, [findDataSources]);

  useEffect(() => {
    if (laneConnectorMode) {
      setCanHightlightEntity(false);
      setCanOpenInfobox(false);
    } else {
      setCanHightlightEntity(true);
      setCanOpenInfobox(true);
    }
  }, [laneConnectorMode, setCanHightlightEntity, setCanOpenInfobox]);

  useEffect(() => {
    try {
      setLaneConnectorAutoAdvance(autoMode);
    } catch {}
  }, [autoMode]);

  const markLayerEdited = useCallback(
    (targetDsKey) => {
      if (typeof setMapObjects !== "function") return;

      const want = targetDsKey === "lanemarker" ? LANE_MARKER_KEY : VIRTUAL_LINE_KEY;

      setMapObjects((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        let changed = false;

        const next = arr.map((obj) => {
          if (changed) return obj;
          if (!obj?.file_name) return obj;
          if (!matchKeyInText(obj.file_name, want)) return obj;
          changed = true;
          return { ...obj, is_edited: true };
        });

        return next;
      });
    },
    [setMapObjects],
  );

  const disableTool = useCallback(() => {
    const { lanemarker, virtualline } = dsRef.current || {};
    try {
      toggleLaneConnectorTool(viewerRef.current, true, lanemarker, virtualline, originalCameraControls);
    } catch {}
  }, [viewerRef, originalCameraControls]);

  const enableTool = useCallback(() => {
    const t = findDataSources();
    dsRef.current = t;

    const hasAny = Boolean(t.lanemarker || t.virtualline);
    if (!hasAny) {
      setLaneConnectorMode(false);
      disableTool();
      return false;
    }

    const radius = Number(settings?.general?.laneConnectorSearchRadiusMeters);
    const angle = Number(settings?.general?.laneConnectorAngleDeg);

    const cfg = {
      searchRadiusMeters: Number.isFinite(radius) ? radius : 40,
      angle: Number.isFinite(angle) ? angle : 7,
    };

    toggleLaneConnectorTool(
      viewerRef.current,
      false,
      t.lanemarker,
      t.virtualline,
      originalCameraControls,
      () => setLaneConnectorMode(false),
      cfg,
      markLayerEdited,
      userData,
      mapObjectsRef,
      setMapObjects,
    );

    try {
      setLaneConnectorAutoAdvance(autoMode);
    } catch {}
    return true;
  }, [findDataSources, viewerRef, originalCameraControls, setLaneConnectorMode, disableTool, settings, markLayerEdited, autoMode]);

  const toggleFully = useCallback(
    (force) => {
      const next = typeof force === "boolean" ? force : !laneConnectorMode;
      if (next) {
        const ok = enableTool();
        if (ok) setLaneConnectorMode(true);
      } else {
        setLaneConnectorMode(false);
        disableTool();
      }
    },
    [laneConnectorMode, enableTool, disableTool, setLaneConnectorMode],
  );

  useEffect(() => {
    if (!laneConnectorMode) disableTool();
  }, [laneConnectorMode, disableTool]);

  useEffect(() => {
    if (!isShow && laneConnectorMode) toggleFully(false);
  }, [isShow, laneConnectorMode, toggleFully]);

  useEffect(() => {
    return () => disableTool();
  }, [disableTool]);

  if (!isShow) return null;

  return (
    <>
      <HStack>
        <Button
          disabled={editMode}
          p={0}
          bg={laneConnectorMode ? "green" : "white"}
          color={laneConnectorMode ? "white" : "MenuText"}
          borderWidth={laneConnectorMode ? 2 : 1}
          borderColor={laneConnectorMode ? "green" : "gray.200"}
          onClick={() => toggleFully()}
          aria-pressed={laneConnectorMode}
          title="Lane Connector"
        >
          <Tooltip content={"Lane Connector"}>
            <LuLink size={22} />
          </Tooltip>
        </Button>
      </HStack>

      <ActionBar.Root open={laneConnectorMode} closeOnEscape={false} closeOnInteractOutside={false}>
        <ActionBar.Positioner>
          <ActionBar.Content>
            <VStack gap={0} align="flex-start">
              <Text fontWeight="semibold" fontSize="sm">
                Lane Connector Tool
              </Text>
              <Separator my={2} width={"full"} />
              <Button
                disabled={editMode || !laneConnectorMode}
                p={0}
                bg={autoMode ? "green" : "white"}
                color={autoMode ? "white" : "MenuText"}
                borderWidth={autoMode ? 2 : 1}
                borderColor={autoMode ? "green" : "gray.200"}
                onClick={() => setAutoMode((p) => !p)}
                aria-pressed={autoMode}
              >
                <Tooltip content={"Lane Connector Auto"}>
                  <LuZap size={22} />
                </Tooltip>
              </Button>
            </VStack>
          </ActionBar.Content>
        </ActionBar.Positioner>
      </ActionBar.Root>
    </>
  );
}

export default LaneConnectorTool;
